import * as angular from 'angular';
import EditLineName, {openEditLine} from "./editLine.controller";
import LineConfigsServiceName, {
  LineConfigsService, LineCounts, LineEquipmentItem, LineItem,
  LineJunctionItem
} from "../../services/lineConfigs/lineConfigs.service";
import {States} from "../../shared/config/constant.module";
import {Plant} from "../../models/navigation";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import template from './lineConfigs.tpl.html';
import NotifyServiceName, {NotifyService} from "../../services/notify/notify.service";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import NavigationServiceName, {NavigationService} from "../../services/navigation/navigation.service";
import AuthDataServiceName, {AuthDataService} from "../../services/auth/authData.service";
import DataPointServiceName, {DataPointService} from "../../services/dataPoints/dataPoints.service";
import EquipmentServiceName, {EquipmentService} from "../../services/equipment/equipment.service";
import UIUtilityServiceName, {UIUtilityService} from "../../services/utility/uiUtility.service";
import * as _ from "lodash";
import {apsCommands} from "../../shared/dto/aps.commands";
import EditLineEquipmentName, {openEditLineEquipment} from "./editLineEquipment.controller";
import EditLineJunctionName, {openEditLineJunction} from "./editLineJunction.controller";

let requires = [EditLineName, LineConfigsServiceName, NotifyServiceName, LoaderServiceName, NavigationServiceName,
  AuthDataServiceName, DataPointServiceName, EquipmentServiceName, UIUtilityServiceName, EditLineEquipmentName,
  EditLineJunctionName];

export default angular.module('aps.controller.lineConfig', requires)
  .config(lineConfigConfig)
  .controller('LineConfigurationCtrl', lineConfigCtrl)
  .name;

function lineConfigConfig($stateProvider: angular.ui.IStateProvider) {
  $stateProvider.state({
    name: States.LINE_CONFIG,
    url: '/admin/lineConfigs',
    controller: 'LineConfigurationCtrl as vm',
    template: template,
    data: {
      pageTitle: 'Line Configuration',
      authorizeAPSIOAdmin: true
    }
  });
}

interface LineConfigurationViewController {
  plants: Plant[];
  shops: apsViewModels.ShopNavigation[];
  plant: Plant;
  shop: apsViewModels.ShopNavigation;
  areas: LineArea[];
  model: LineCounts;
  lineEquipmentNames: apsViewModels.LineEquipmentNameItem[];
  equipment: apsViewModels.EquipmentDetail[];
  dataPoints: apsViewModels.DataPointDetail[];

  editLineConfiguration(lineConfig: apsViewModels.ShopInspectionLines): void;
  plantChanged(skipShopChanged?: boolean): void;
  shopChanged(): void;

  addLine(lineJunction: apsViewModels.LineJunctionItem): void;
  addLineEquipment(line: LineItem): void;
  addLineJunction(line: LineItem): void;

  editLine(line: LineItem): void;
  editLineEquipment(lineEquipment: apsViewModels.LineEquipmentItem): void;
  editLineJunction(lineJunction: apsViewModels.LineJunctionItem): void;

  removeLineEquipment(line: LineItem, lineEquipment: apsViewModels.LineEquipmentItem): void;
  removeLine(line: LineItem): void;
  removeLineJunction(lineJunction: LineJunctionItem): void;

  toggleLine(line: LineItem): void;
  toggleLineEquipment(line: LineItem, lineEquipment: apsViewModels.LineEquipmentItem): void;

  moveLineEquipmentLeft(lineEquipment: apsViewModels.LineEquipmentItem): void;
  moveLineEquipmentRight(lineEquipment: apsViewModels.LineEquipmentItem): void;
}

export interface LineRow {
  isLine: boolean;
  line: LineItem;
  junction: LineJunctionItem;
  cssClass: string;
}

export interface LineArea {
  rows: LineRow[];
}

let mapLineToRow = (line: LineItem): LineRow => {
  return {
    isLine: true,
    line: line,
    junction: null,
    cssClass: null
  };
};

let mapJunctionToRow = (junction: LineJunctionItem): LineRow => {
  return {
    isLine: false,
    line: null,
    junction: junction,
    cssClass: 'addtable' + (junction.inputs.length > junction.outputs.length
      ? junction.inputs.length : junction.outputs.length).toString()
  };
};

let mapModelToAreas = (model: LineCounts): LineArea[] => {
  let areas = [];
  let area: LineArea = {
    rows: _.sortBy(model.lines.filter(l => l.startJunctionId === null), r => r.number)
      .map(l => mapLineToRow(l))
  };

  while (area.rows.length > 0) {
    areas.push(area);

    let previousLines = area.rows.filter(l => l.isLine).map(l => l.line);
    let rows = [];
    area.rows.forEach(row => {
      // if row is junction, add all of the output lines
      if (!row.isLine) {
        _.sortBy(row.junction.outputs, l => l.number).forEach(line => {
          rows.push(mapLineToRow(line));
        });
        return;
      }

      let junction = row.line.endJunction;
      if (!junction) {
        return;
      }

      // do not add the junction if it is already in the list of rows
      if (_.some(rows, r => !r.isLine && r.junction === junction)) {
        return;
      }

      // if the end junction references lines not in the previous area, add the line instead
      if (!junction.inputs.every(l => _.some(previousLines, al => al.id === l.id))) {
        rows.push(mapLineToRow(row.line));
      }

      rows.push(mapJunctionToRow(junction));
    });

    area = {rows: rows};
  }
  return areas;
};

function lineConfigCtrl(
  notifyService: NotifyService,
  lineConfigsService: LineConfigsService,
  loaderService: LoaderService,
  navigationService: NavigationService,
  authDataService: AuthDataService,
  dataPointService: DataPointService,
  equipmentService: EquipmentService,
  $q: angular.IQService,
  $modal: mm.foundation.modal,
  uiUtilityService: UIUtilityService
): LineConfigurationViewController {

  let vm = {} as LineConfigurationViewController;

  let lineEquipmentNamesPromise = lineConfigsService.getLineEquipmentNames().then(names =>
    vm.lineEquipmentNames = names
  );

  let equipmentPromise: angular.IPromise<any>;

  let dataPointsPromise: angular.IPromise<any>;

  let loadLineConfigs = () => {
    let lineConfigsPromise = lineConfigsService.getLineConfiguration(vm.shop.id)
      .then((result: LineCounts) => {
        vm.model = result;
        vm.areas = mapModelToAreas(vm.model);
      });

    loaderService.loadingPromise(lineConfigsPromise);
    return;
  };

  let determineAllowedLineEquipmentMovement = (lineEquipment: LineEquipmentItem,
                                               minOrder: number,
                                               maxOrder: number
  ): void => {
    lineEquipment.canMoveLeft = lineEquipment.order > minOrder;
    lineEquipment.canMoveRight = lineEquipment.order < maxOrder;
  };

  let updateLineEquipmentOrder = (movedEquipment: apsViewModels.LineEquipmentItem, newOrder: number) => {
    let newOrderIsIncreasing = movedEquipment.order < newOrder;
    let line = _.find(vm.model.lines, {id: movedEquipment.lineId});
    let minOrder = _.min(_.map(line.equipment, 'order'));
    let maxOrder = _.max(_.map(line.equipment, 'order'));

    line.equipment.forEach(equipment => {
      if (equipment.id === movedEquipment.id) {
        return;
      }
      if (newOrderIsIncreasing) {
        if (equipment.order <= newOrder && equipment.order > movedEquipment.order) {
          equipment.order -= 1;
        }
      } else {
        if (equipment.order >= newOrder && equipment.order < movedEquipment.order) {
          equipment.order += 1;
        }
      }
      determineAllowedLineEquipmentMovement(equipment, minOrder, maxOrder);
    });

    let movedEquipmentInstance = _.assign({
      canMoveLeft: false,
      canMoveRight: false
    }, movedEquipment);
    movedEquipmentInstance.order = newOrder;
    determineAllowedLineEquipmentMovement(movedEquipmentInstance, minOrder, maxOrder);
    line.equipment = _.sortBy(line.equipment, 'order');
  };

  let launchLineEquipmentAdd = (line: LineItem) => {
    $q.all({
      equipmentNames: lineEquipmentNamesPromise,
      equipment: equipmentPromise,
      dataPoints: dataPointsPromise
    }).then(() => {
      let result = openEditLineEquipment({
        lineEquipment: undefined,
        line: line,
        shop: vm.shop,
        lineEquipmentNames: vm.lineEquipmentNames,
        equipment: vm.equipment.filter(e => e.plantLocationId === vm.shop.plantLocationId),
        dataPoints: vm.dataPoints.filter(e => e.plantLocationId === vm.shop.plantLocationId)
      }, $modal);

      result.then((addedLineEquipment) => {
        if (!addedLineEquipment) {
          return;
        }

        if (!line.equipment) {
          line.equipment = [];
        }
        line.equipment.push(addedLineEquipment);
        lineConfigsService.updateEquipment(line);
        vm.areas = mapModelToAreas(vm.model);
      });
    });
  };

  let launchLineEquipmentEdit = (lineEquipment: apsViewModels.LineEquipmentItem) => {
    $q.all({
      equipmentNames: lineEquipmentNamesPromise,
      equipment: equipmentPromise,
      dataPoints: dataPointsPromise
    }).then(() => {
      let result = openEditLineEquipment({
        lineEquipment: lineEquipment,
        line: undefined,
        shop: vm.shop,
        lineEquipmentNames: vm.lineEquipmentNames,
        equipment: vm.equipment.filter(e => e.plantLocationId === vm.shop.plantLocationId),
        dataPoints: vm.dataPoints.filter(e => e.plantLocationId === vm.shop.plantLocationId)
      }, $modal);

      result.then((editedLineEquipment) => {
        if (!editedLineEquipment) {
          return;
        }

        _.assign(lineEquipment, editedLineEquipment);
      });
    });
  };

  let getEligibleEndJunctions = (lineJunction?: LineJunctionItem)
    : LineJunctionItem[] => {

    let junctions = [];
    if (!lineJunction) {
      return junctions;
    }

    lineJunction.outputs.forEach(line => {
      if (line.endJunction && junctions.indexOf(line.endJunction) < 0) {
        junctions.push(line.endJunction);
      }
    });

    return junctions;
  };

  let launchLineAdd = (lineJunction?: LineJunctionItem) => {
    let result = openEditLine({
      line: undefined,
      shop: vm.shop,
      startJunction: lineJunction,
      eligibleEndJunctions: getEligibleEndJunctions(lineJunction)
    }, $modal);

    result.then((addedLine) => {
      if (addedLine) {
        vm.model.lines.push(addedLine);
        if (addedLine.startJunctionId) {
          let lineJunction = _.find(vm.model.junctions, {id: addedLine.startJunctionId});
          if (lineJunction) {
            addedLine.startJunction = lineJunction;
            lineJunction.outputs.push(addedLine);
          }
        }
        loadLineConfigs();
      }
    });
  };

  let launchLineEdit = (line: LineItem) => {
    let result = openEditLine({
      line: line,
      shop: vm.shop,
      startJunction: line.startJunction,
      eligibleEndJunctions: getEligibleEndJunctions(line.startJunction)
    }, $modal);

    result.then((editedLine) => {
      if (editedLine) {
        if (line) {
          _.assign(line, editedLine);
          vm.areas = mapModelToAreas(vm.model);
          return;
        }
        vm.model.lines.push(editedLine);
        if (editedLine.startJunctionId) {
          let lineJunction = _.find(vm.model.junctions, {id: editedLine.startJunctionId});
          if (lineJunction) {
            editedLine.startJunction = lineJunction;
            lineJunction.outputs.push(editedLine);
          }
        }
        vm.areas = mapModelToAreas(vm.model);
      }
    });
  };

  let launchLineJunctionAdd = (line: LineItem) => {
    let result = openEditLineJunction({
      lineJunction: undefined,
      line: line,
      shop: vm.shop,
      lineConfig: vm.model
    }, $modal);

    result.then((editedLineJunction) => {
      if (editedLineJunction) {
        editedLineJunction.inputs.forEach(line => {
          line.endJunction = editedLineJunction;
          line.endJunctionId = editedLineJunction.id;

          let modelLines = _.filter(vm.model.lines, {id: line.id});
          if (modelLines) {
            modelLines.forEach(mLine => {
              mLine.endJunction = editedLineJunction;
              mLine.endJunctionId = editedLineJunction.id;
            });
          }
        });

        if (!editedLineJunction.outputs) {
          editedLineJunction.outputs = [];
        }

        if (!vm.model.junctions) {
          vm.model.junctions = [];
        }
        vm.model.junctions.push(editedLineJunction);
        vm.areas = mapModelToAreas(vm.model);
      }
    });
  };

  let launchLineJunctionEdit = (lineJunction: apsViewModels.LineJunctionItem) => {
    let result = openEditLineJunction({
      lineJunction: lineJunction,
      line: undefined,
      shop: vm.shop,
      lineConfig: vm.model
    }, $modal);

    result.then((editedLineJunction) => {
      if (editedLineJunction) {
        editedLineJunction.inputs.forEach(line => {
          line.endJunction = editedLineJunction;
          line.endJunctionId = editedLineJunction.id;

          let modelLines = _.filter(vm.model.lines, {id: line.id});
          if (modelLines) {
            modelLines.forEach(mLine => {
              mLine.endJunction = editedLineJunction;
              mLine.endJunctionId = editedLineJunction.id;
            });
          }
        });

        _.assign(lineJunction, editedLineJunction);
        vm.areas = mapModelToAreas(vm.model);
      }
    });
  };

  let getPlantShopsDataPromise = navigationService.getNavigation()
    .then(navModel => {
      let plantsList = navModel.plants();
      vm.plants = _.filter(plantsList, (plant: Plant) => {
        return authDataService.isPlantAdmin(plant.id) || authDataService.isAPSIOAdminForPlant(plant.id);
      });

      if (navModel.selectedPlant && _.includes(vm.plants, navModel.selectedPlant)) {
        vm.plant = navModel.selectedPlant;
      } else {
        vm.plant = _.first(vm.plants);
      }

      vm.plantChanged(true);

      vm.shops = vm.plant.shops;

      if (navModel.selectedShop) {
        vm.shop = navModel.selectedShop;
      }

      loadLineConfigs();
    });

  vm.plantChanged = (skipShopChanged?: boolean) => {
    vm.shop = _.first(vm.plant.shops);
    dataPointsPromise = dataPointService.get(vm.plant.id).then(data => {
      vm.dataPoints = data;
    });
    equipmentPromise = equipmentService.get(vm.plant.id).then(data => {
      vm.equipment = data;
    });
    if (skipShopChanged) {
      return;
    }
    vm.shopChanged();
  };

  vm.shopChanged = () => {
    loadLineConfigs();
  };

  vm.addLineEquipment = launchLineEquipmentAdd;

  vm.editLineEquipment = (lineEquipment: apsViewModels.LineEquipmentItem) => {
    launchLineEquipmentEdit(lineEquipment);
  };

  vm.addLine = (lineJunction: LineJunctionItem) => {
    launchLineAdd(lineJunction);
  };

  vm.editLine = (line: LineItem) => {
    launchLineEdit(line);
  };

  vm.addLineJunction = (line: LineItem) => {
    launchLineJunctionAdd(line);
  };

  vm.editLineJunction = (lineJunction: LineJunctionItem) => {
    launchLineJunctionEdit(lineJunction);
  };

  vm.removeLine = (line: LineItem) => {
    let message = `Are you sure you want to remove Line ${line.name}?`;
    let subject = 'Confirm Line Removal';
    let modalInstanceRemoveLine = uiUtilityService.confirm(message, subject);

    modalInstanceRemoveLine.then(confirmed => {
      if (!confirmed) {
        return;
      }

      let removeLinePromise = lineConfigsService.removeLine({
        id: line.id,
        shopId: line.shopId
      }).then(() => {
        _.remove(vm.model.lines, {id: line.id});
        let startJunction = line.startJunction;
        if (startJunction) {
          _.remove(startJunction.outputs, line);
        }

        vm.areas = mapModelToAreas(vm.model);
      }, notifyService.handleApiErrors);

      loaderService.loadingPromise(removeLinePromise);
    });
  };

  vm.removeLineEquipment = (line: LineItem,
                            lineEquipment: apsViewModels.LineEquipmentItem) => {
    let message = `Are you sure you want to remove ${lineEquipment.name}?`;
    let subject = `Confirm Remove Equipment From Line ${line.name}`;
    let modalInstanceRemoveLineEquipment = uiUtilityService.confirm(message, subject);

    modalInstanceRemoveLineEquipment.then(confirmed => {
      if (!confirmed) {
        return;
      }

      let removeLineEquipmentPromise = lineConfigsService.removeLineEquipment({
        shopId: lineEquipment.shopId,
        id: lineEquipment.id
      })
        .then(() => {
          _.remove(line.equipment, {id: lineEquipment.id});
          lineConfigsService.updateEquipment(line);
        }, notifyService.handleApiErrors);

      loaderService.loadingPromise(removeLineEquipmentPromise);
      return;
    });
  };

  vm.removeLineJunction = (lineJunction: LineJunctionItem) => {
    let message = `Are you sure you want to remove Line Junction ${lineJunction.name}?`;
    let subject = 'Confirm Line Junction Removal';
    let modalInstanceRemoveLineJunction = uiUtilityService.confirm(message, subject);

    modalInstanceRemoveLineJunction.then(confirmed => {
      if (!confirmed) {
        return;
      }
      let removeLineJunctionPromise = lineConfigsService.removeLineJunction({
        id: lineJunction.id,
        shopId: lineJunction.shopId
      }).then(() => {
        _.remove(vm.model.junctions, {id: lineJunction.id});
        lineJunction.inputs.forEach(line => {
          line.endJunctionId = null;
          line.endJunction = null;
        });
        vm.areas = mapModelToAreas(vm.model);
      }, notifyService.handleApiErrors);

      loaderService.loadingPromise(removeLineJunctionPromise);
    });
  };

  vm.toggleLine = (line: LineItem) => {
    let toggleType = line.isDisabled ? 'Disable' : 'Enable';
    let message = `Are you sure you want to ${toggleType.toLowerCase()} Line ${line.name}?`;
    let subject = `${toggleType} Line`;
    let modalInstanceToggleLine = uiUtilityService.confirm(message, subject);
    let revertState = () => line.isDisabled = !line.isDisabled;

    modalInstanceToggleLine.then(confirmed => {
      if (confirmed) {
        let command = {id: line.id, shopId: line.shopId, isDisabled: line.isDisabled};
        let toggleLinePromise = lineConfigsService.toggleLine(command).then(() => {
          // do nothing; subsequent actions are handled though angular's watchers
        }, (errors) => {
          notifyService.handleApiErrors(errors);
          revertState();
        });

        loaderService.loadingPromise(toggleLinePromise);
      } else {
        // action has been cancelled; revert model value
        revertState();
      }
    });
  };

  vm.toggleLineEquipment = (line: LineItem,
                            lineEquipment: apsViewModels.LineEquipmentItem) => {
    let toggleType = lineEquipment.isDisabled ? 'Disable' : 'Enable';
    let message = `Are you sure you want to ${toggleType.toLowerCase()} Equipment ${lineEquipment.name} on Line ` +
      `${line.name}?`;
    let subject = `${toggleType} Line Equipment`;
    let modalInstanceToggleLineEquipment = uiUtilityService.confirm(message, subject);
    let revertState = () => lineEquipment.isDisabled = !lineEquipment.isDisabled;

    modalInstanceToggleLineEquipment.then(confirmed => {
      if (confirmed) {
        let command: apsCommands.ToggleLineEquipment = {
          id: lineEquipment.id,
          lineId: lineEquipment.lineId,
          shopId: lineEquipment.shopId,
          isDisabled: lineEquipment.isDisabled
        };
        let toggleLineEquipmentPromise = lineConfigsService.toggleLineEquipment(command).then(() => {
          // do nothing; subsequent actions are handled though angular's watchers
        }, (errors) => {
          notifyService.handleApiErrors(errors);
          revertState();
        });

        loaderService.loadingPromise(toggleLineEquipmentPromise);
      } else {
        // action has been cancelled; revert model value
        revertState();
      }
    });
  };

  let reorderLineEquipment = (movedEquipment: apsViewModels.LineEquipmentItem, newOrder: number) => {
    let command = {
      shopId: movedEquipment.shopId,
      lineId: movedEquipment.lineId,
      id: movedEquipment.id,
      newOrder: newOrder
    };
    lineConfigsService.reorderLineEquipment(command).then(() => {
      updateLineEquipmentOrder(movedEquipment, newOrder);
    });
  };

  vm.moveLineEquipmentLeft = (lineEquipment: apsViewModels.LineEquipmentItem) => {
    reorderLineEquipment(lineEquipment, lineEquipment.order - 1);
  };

  vm.moveLineEquipmentRight = (lineEquipment: apsViewModels.LineEquipmentItem) => {
    reorderLineEquipment(lineEquipment, lineEquipment.order + 1);
  };

  loaderService.loadingPromise(getPlantShopsDataPromise);

  return vm;
}


// WEBPACK FOOTER //
// ./src/app/controllers/lineConfigs/lineConfigs.controller.ts